import { useReveal } from "../../../hooks/useReveal";

const values = [
  { icon: "ri-heart-line", title: "Genuine Connection", desc: "Every session begins with a real human connection. Stephanie takes time to get to know you so your images reflect who you truly are." },
  { icon: "ri-leaf-line", title: "Calm Presence", desc: "Whether photographing a fussy newborn or nervous wedding couple, Stephanie's grounding energy makes everyone feel at ease instantly." },
  { icon: "ri-medal-line", title: "Uncompromising Quality", desc: "15+ years of mastering light, composition, and emotion means every frame is crafted with intention — never rushed, never careless." },
  { icon: "ri-home-heart-line", title: "Family-First Philosophy", desc: "As a photographer who deeply values family, Stephanie brings a personal tenderness to every newborn, wedding, and portrait session." },
];

export default function StephanieStory() {
  const ref1 = useReveal<HTMLElement>();
  const ref2 = useReveal<HTMLElement>();

  return (
    <>
      {/* Story Section */}
      <section ref={ref1} className="bg-pk-ivory py-28 overflow-hidden">
        <div className="max-w-screen-xl mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
            {/* Text */}
            <div className="reveal-left">
              <span className="font-sans text-xs tracking-widest3 uppercase text-pk-gold">Our Story</span>
              <h2 className="font-display text-5xl md:text-6xl font-medium text-pk-charcoal mt-4 leading-tight">
                A Studio Built on<br /><em>Connection &amp; Craft</em>
              </h2>
              <div className="w-10 h-px bg-pk-gold mt-6 mb-7" />
              <p className="font-sans text-sm text-pk-muted leading-7">
                I believe the most meaningful photos come from genuine connection. Every newborn, wedding, and portrait session is guided with care, so you can feel relaxed, confident, and fully present in every moment.
              </p>
              <p className="font-sans text-sm text-pk-muted mt-4 leading-7">
                I provide a professional, detail-oriented experience rooted in genuine care. I focus on building a real connection with you — ensuring your session is as stress-free as it is beautiful.
              </p>
              <p className="font-sans text-sm text-pk-muted mt-4 leading-7">
                Based in the vibrant city of San Antonio, Texas, Precious Keepsakes Photography has served hundreds of families across the city and surrounding Hill Country region. Each image is a promise — that these moments will live on long after the day has passed.
              </p>
              <div className="mt-10 flex items-end gap-5">
                <img
                  src="https://images-pw.pixieset.com/site/BAJyQr/9DopZA/signature1-9e15dd4b-1500.png"
                  alt="Stephanie signature"
                  className="h-14 w-auto object-contain"
                />
                <div>
                  <p className="font-sans text-xs font-semibold text-pk-charcoal uppercase tracking-widest">Stephanie</p>
                  <p className="font-sans text-xs text-pk-muted mt-0.5">Founder &amp; Lead Photographer</p>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="reveal-right relative">
              <div className="w-full h-[560px] overflow-hidden rounded-sm img-hover-zoom">
                <img
                  src="https://images-pw.pixieset.com/site/BAJyQr/DDl4Qv/004-20241021-ea579869-1500.jpg"
                  alt="Precious Keepsakes Photography — newborn session San Antonio"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Offset border accent */}
              <div className="absolute -bottom-5 -right-5 w-full h-full border border-pk-gold/15 rounded-sm -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section ref={ref2} className="bg-pk-charcoal py-24 overflow-hidden">
        <div className="max-w-screen-xl mx-auto px-8">
          <div className="reveal text-center mb-16">
            <span className="font-sans text-xs tracking-widest3 uppercase text-pk-gold">What We Stand For</span>
            <h2 className="font-display text-5xl font-medium text-white mt-4 leading-tight">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/5 rounded-sm overflow-hidden">
            {values.map((v, i) => (
              <div
                key={i}
                className="reveal bg-pk-charcoal p-10 group hover:bg-pk-dark transition-colors duration-500"
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-pk-gold/10 rounded-sm mb-6 group-hover:bg-pk-gold/20 transition-colors duration-500">
                  <i className={`${v.icon} text-pk-gold text-2xl`} />
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-3">{v.title}</h3>
                <p className="font-sans text-xs text-white/50 leading-6">{v.desc}</p>
                <div className="mt-6 w-0 group-hover:w-10 h-px bg-pk-gold transition-all duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
